import { useState } from "react";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import GoogleAuth from "../components/GoogleAuth";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  serverTimestamp,
  setDoc,
  addDoc,
  collection,
} from "firebase/firestore";
import {
  MantineProvider,
  createTheme,
  Checkbox,
  Text,
  TextInput,
  Button,
} from "@mantine/core";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import TermsAndConditionsModal from "../components/TermsAndConditionsModal";
//import KeysImage from "../assets/images/signinup.png";
//import KeysImage from "../assets/images/signinup_modern.png";
//import KeysImage from "../assets/images/signinup_oldstyle.png";
import { updateCurrentUser } from "./ProfileUtil";
import { getChatId } from "../components/ChatUtil";
import {
  checkConfirmPassowrd,
  sanitizeInput,
  validateEmail,
  validatePassword,
  validateUserName,
} from "../utils/helpers";

const theme = createTheme({
  cursorType: "pointer",
});

export default function SignUp() {
  const navigate = useNavigate();
  const intl = useIntl();

  const welcomeMessage = intl.formatMessage({ id: "welcome.message" });

  const [showPassword, setShowPassword] = useState(false);
  const [termsAndConditionsChecked, setTermsAndConditionsChecked] =
    useState(false);
  const [termsAndConditionsModalOpen, setTermsAndConditionsModalOpen] =
    useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const { name, email, password, confirmPassword } = formData;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    let sanitizedValue = sanitizeInput(value);
    let error = "";

    // Apply specific sanitization and validation
    if (id === "name") {
      if (!validateUserName(value)) {
        error = "Name must be 1-100 characters, with no special symbols.";
      }
    } else if (id === "email") {
      if (!validateEmail(value)) {
        error = "Please enter a valid email address.";
      }
    } else if (id === "password" || id === "confirmPassword") {
      if (!validatePassword(value)) {
        error =
          "Password must be 8-128 characters, with at least one letter, one number, and one special symbol.";
      }
      if (!checkConfirmPassowrd(value, formData.password)) {
        error = "Password and confirm password must be the same";
      }
    } else if (sanitizedValue !== value) {
      error = "Please remove all special symbols from the field.";
    }

    setErrors((prevState) => ({
      ...prevState,
      [id]: error,
    }));
  };

  const sendWelcomeMessage = async () => {
    if (!auth.currentUser) return;
    await addDoc(collection(db, "messages"), {
      text: welcomeMessage,
      name: "Soft Retired",
      avatar: "",
      createdAt: new Date().toISOString(),
      senderId: process.env.REACT_APP_SR_USER_ID,
      recipientId: auth.currentUser.uid,
      chatId: getChatId(process.env.REACT_APP_SR_USER_ID, auth.currentUser.uid),
    });
  };

  async function onSubmit(e: any) {
    if (
      !errors.name &&
      !errors.email &&
      !errors.password &&
      !errors.confirmPassword
    ) {
      e.preventDefault();
      if (password === confirmPassword) {
        try {
          const auth = getAuth();
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            sanitizeInput(email),
            password
          );

          if (!auth.currentUser) return;

          const user = userCredential.user;

          await setDoc(doc(db, "users", user.uid), {
            email: sanitizeInput(formData.email),
            createdDate: serverTimestamp(),
          });
          await updateCurrentUser(
            sanitizeInput(formData.name),
            "",
            "",
            "looking",
            "",
            "5-10",
            "",
            "0",
            auth.currentUser
          );

          sendWelcomeMessage();

          navigate("/myprofile");
        } catch (error) {
          toast.error("Something went wrong with the registration");
        }
      } else
        toast.error(
          "Please make sure that your password and the confirmed password are the same."
        );
    } else {
      toast.error("Please fix the errors before submitting.");
    }
  }

  const handleTermsAndCondClick = () => {
    setTermsAndConditionsModalOpen(true);
  };

  const handleOnCloseTermsAndCondModal = () => {
    setTermsAndConditionsModalOpen(false);
  };

  const termsAndConditionsModal = termsAndConditionsModalOpen ? (
    <TermsAndConditionsModal
      opened={termsAndConditionsModalOpen}
      onClose={handleOnCloseTermsAndCondModal}
    />
  ) : null;

  return (
    <>
      {termsAndConditionsModal}
      <section className="signup-section">
        <h1 className="signup-heading">
          {intl.formatMessage({ id: "signup.signUp" })}
        </h1>
        <div className="signup-container">
          <div className="signup-form-container">
            <form onSubmit={onSubmit}>
              <TextInput
                type="text"
                id="name"
                value={name}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={intl.formatMessage({ id: "signup.name" })}
                className="signup-input"
                maxLength={100}
                error={errors.name}
              />
              <TextInput
                type="email"
                id="email"
                value={email}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={intl.formatMessage({ id: "signup.email" })}
                className="signup-input"
                maxLength={100}
                error={errors.email}
              />
              <div className="password-container">
                <TextInput
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={intl.formatMessage({ id: "signup.password" })}
                  className="signup-input"
                  maxLength={100}
                  error={errors.password}
                />
                {!showPassword ? (
                  <AiFillEyeInvisible
                    className="password-toggle"
                    onClick={() => setShowPassword((prevState) => !prevState)}
                  />
                ) : (
                  <AiFillEye
                    className="password-toggle"
                    onClick={() => setShowPassword((prevState) => !prevState)}
                  />
                )}
              </div>
              <div className="password-container">
                <TextInput
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={intl.formatMessage({
                    id: "signup.confirmPassword",
                  })}
                  className="signup-input"
                  maxLength={100}
                  error={errors.confirmPassword}
                />
                {!showPassword ? (
                  <AiFillEyeInvisible
                    className="password-toggle"
                    onClick={() => setShowPassword((prevState) => !prevState)}
                  />
                ) : (
                  <AiFillEye
                    className="password-toggle"
                    onClick={() => setShowPassword((prevState) => !prevState)}
                  />
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "inherit",
                }}
              >
                <MantineProvider theme={theme}>
                  <Checkbox
                    label={intl.formatMessage({ id: "signup.iAccept" })}
                    checked={termsAndConditionsChecked}
                    onChange={(event) =>
                      setTermsAndConditionsChecked(event.currentTarget.checked)
                    }
                    styles={{
                      label: { fontSize: "18px" },
                      input: {
                        backgroundColor: "#0abab5", // unchecked background
                        borderColor: "#0abab5", // border color
                        "&:checked": {
                          backgroundColor: "#0abab5", // checked background
                          borderColor: "#0abab5", // checked border
                        },
                      },
                    }}
                  />
                </MantineProvider>
                <Text
                  component="span"
                  onClick={handleTermsAndCondClick}
                  style={{
                    color: "blue",
                    cursor: "pointer",
                    marginLeft: 5,
                    paddingTop: "3px",
                    fontSize: "18px",
                  }}
                >
                  {intl.formatMessage({ id: "signup.termsAndConditions" })}
                </Text>
              </div>
              <div className="signup-divider"></div>
              <Button
                fullWidth
                className="signup-button"
                type="submit"
                disabled={
                  !termsAndConditionsChecked ||
                  errors.name !== "" ||
                  errors.email !== "" ||
                  errors.password !== "" ||
                  errors.confirmPassword !== ""
                }
              >
                {intl.formatMessage({ id: "signup.signUp" })}
              </Button>
              <div className="signup-divider">
                <p className="signup-divider-text">
                  {intl.formatMessage({ id: "signin.or" })}
                </p>
              </div>
              <GoogleAuth
                disabled={
                  !termsAndConditionsChecked ||
                  errors.name !== "" ||
                  errors.email !== ""
                }
              />
              <div className="signup-links">
                <p className="signup-link">
                  {intl.formatMessage({ id: "signup.haveAccount" })}
                  <Link to="/sign-in" className="signup-link-signin">
                    {intl.formatMessage({ id: "signin.signIn" })}
                  </Link>
                </p>
                <p>
                  <Link to="/forgot-password" className="signup-link-forgot">
                    {intl.formatMessage({ id: "signin.forgotPassword" })}
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
